/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import { validateScheduleDay } from '@ps-design-system/components/ScheduleInputField';
import { reject } from 'lodash';
import Cookies from 'react-cookie';
import Calendar from '../../../../api/api-calendar';
import Contracts from '../../../../api/api-contracts';
import { getTagActivities } from '../../../../api/api-env';
import { Fees } from '../../../../api/api-fees';
import { Inventory } from '../../../../api/api-inventory';
import { Listings } from '../../../../api/api-listings';
import {
  createBookingRequestPayload,
  createIntentRequestPayload,
  createCheckoutFormDefaultValues,
  createQuoteFromContractsResponse,
  createQuotePayload,
  getPaymentAmount
} from '../checkout-utils';
import { logError } from './logging-service';
import { confirmPaymentIntent, createSetupIntent } from './payment-service';

export const fetchListing = (listingId) => {
  return new Promise((resolve, reject) => {
    Listings.read(listingId, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('checkout-service.fetchListing', response);
        reject(response);
        return;
      }
      resolve(response);
    });
  });
};

export const fetchInventory = (accessToken, spaceId) => {
  return new Promise((resolve, reject) => {
    Inventory.read(spaceId, accessToken, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('checkout-service.fetchInventory', response);
        return reject(response);
      }
      resolve(response);
    });
  });
};

export const fetchListingWithInventory = async (accessToken, listingId) => {
  const listing = await fetchListing(listingId);
  const inventoryRes = await fetchInventory(accessToken, listing.parentSpaceId);
  return { listing, inventory: inventoryRes?.inventory };
};

export const fetchContractsSnapshot = (accessToken, bookingId) => {
  return new Promise((resolve, reject) => {
    Contracts.readContractsSnapshot(bookingId, accessToken, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('checkout-service.fetchContractsSnapshot', response);
        reject(response);
        return;
      }
      resolve(response);
    });
  });
};

export const fetchBookingQuote = (accessToken, payload) => {
  return new Promise((resolve, reject) => {
    Contracts.guestRequestQuote(accessToken, payload, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('checkout-service.fetchBookingQuote', response);
        reject(response);
        return;
      }
      resolve(createQuoteFromContractsResponse(response));
    });
  });
};

export const fetchExistingQuote = async (
  accessToken,
  listingId,
  existingBookingId
) => {
  let listing;
  let inventory;
  try {
    ({ listing, inventory } = await fetchListingWithInventory(
      accessToken,
      listingId
    ));

    const contractSnapshot = await fetchContractsSnapshot(
      accessToken,
      existingBookingId
    );

    const latestContracts = contractSnapshot?.pending?.length
      ? contractSnapshot?.pending
      : contractSnapshot?.contracts || [];

    const spaceRentalContractSnapshot = latestContracts.find(
      (contract) => contract.type === 'SPACE_RENTAL'
    );

    const { booking } = spaceRentalContractSnapshot?.meta || {};

    const validScheduleDays = (booking?.schedule || []).filter((day) =>
      validateScheduleDay(day)
    );

    if (!validScheduleDays.length) {
      return {
        inventory,
        listing,
        quote: {
          masterContract: null,
          splitPaymentPermitted: false,
          spaceRentalContract: null,
          guestPaymentDetails: null,
          addOnsContracts: []
        }
      };
    }

    // override booking schedule with valid schedule days
    booking.schedule = validScheduleDays;

    const masterContractSnapshot = latestContracts.find(
      (contract) => contract.type === 'MASTER'
    );

    const addOnsContractsSnapshot = latestContracts.filter(
      (contract) => contract.type === 'ITEM'
    );

    const quoteSnapshot = {
      masterContract: masterContractSnapshot,
      spaceRentalContract: spaceRentalContractSnapshot,
      addOnsContracts: addOnsContractsSnapshot
    };

    // we need to fetch a new quote here to determine if split payment is still permitted
    // @TODO: see if backend can include this in snapshot response
    const newQuote = await fetchBookingQuote(
      accessToken,
      createQuotePayload({
        listing,
        SSOid: booking.guest_id,
        formValues: createCheckoutFormDefaultValues({
          quote: quoteSnapshot,
          inventory
        })
      })
    );
    return {
      inventory,
      listing,
      quote: newQuote
    };
  } catch (error) {
    return {
      error,
      inventory,
      listing,
      quote: createQuoteFromContractsResponse(null)
    };
  }
};

export const fetchInitialQuote = async (accessToken, SSOid, listingId) => {
  let listing;
  let inventory;
  try {
    ({ listing, inventory } = await fetchListingWithInventory(
      accessToken,
      listingId
    ));

    const scheduleFromCookie = Cookies.load('booking_schedule') || [];
    const validScheduleDays = scheduleFromCookie.filter((day) =>
      validateScheduleDay(day)
    );

    if (!validScheduleDays.length) {
      Cookies.remove('booking_schedule');

      return {
        inventory,
        listing,
        quote: createQuoteFromContractsResponse(null)
      };
    }

    if (validScheduleDays.length !== scheduleFromCookie.length) {
      Cookies.set('booking_schedule', validScheduleDays);
    }

    const quote = await fetchBookingQuote(
      accessToken,
      createQuotePayload({
        listing,
        SSOid,
        formValues: {
          bookingDetails: {
            schedule: validScheduleDays
          }
        }
      })
    );

    return {
      inventory,
      quote,
      listing
    };
  } catch (error) {
    return {
      error,
      inventory,
      listing,
      quote: createQuoteFromContractsResponse(null)
    };
  }
};

export const fetchActivityTags = (locale) => {
  return new Promise((resolve, reject) => {
    getTagActivities(locale, (response) => {
      if (response.error || response.statusCode >= 400) {
        logError('checkout-service.fetchActivityTags', response);
        reject(response);
        return;
      }

      resolve(response);
    });
  });
};

export const createBookingFromInquiry = (accessToken, bookingId, payload) => {
  return new Promise((resolve, reject) => {
    Contracts.inquiryToRequest(bookingId, payload, accessToken, (response) => {
      if (response?.error || response?.statusCode >= 400) {
        logError('checkout-service.createBookingFromInquiry', response);
        return reject(response);
      }

      resolve(response);
    });
  });
};

export const createBooking = (accessToken, payload) => {
  return new Promise((resolve, reject) => {
    Contracts.guestRequest(payload, accessToken, (response) => {
      if (response?.error || response?.statusCode >= 400) {
        logError('checkout-service.createBooking', response);
        return reject(response);
      }

      return resolve(response);
    });
  });
};

export const postBookingRequest = (accessToken, payload, existingBookingId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const createBookingRes = existingBookingId
        ? await createBookingFromInquiry(
            accessToken,
            existingBookingId,
            payload
          )
        : await createBooking(accessToken, payload);

      resolve(createQuoteFromContractsResponse(createBookingRes));
    } catch (error) {
      reject(error);
    }
  });
};

export const postBillingAddress = (accessToken, SSOid, billingAddress) => {
  return new Promise((resolve) => {
    Fees.createBillingAddress(
      accessToken,
      SSOid,
      {
        sso_id: SSOid,
        type: 'BILLING',
        country: billingAddress?.country,
        state: billingAddress?.state,
        city: billingAddress?.city,
        street: billingAddress?.address1,
        zip: billingAddress?.zip
      },
      (response) => {
        if (response.error || response.statusCode >= 400) {
          logError('checkout-service.postBillingAddress', response);
          return reject(response);
        }

        resolve(response);
      }
    );
  });
};

export const fetchCalendarPricing = (accessToken, listingId) => {
  return new Promise((resolve, reject) => {
    Calendar.readCalendar(
      `prices/listing/${listingId}`,
      accessToken,
      (response) => {
        if (response.error || response.statusCode >= 400) {
          logError('checkout-service.fetchCalendarPricing', response);
          return reject(response);
        }
        resolve(response?.prices_by_group || []);
      }
    );
  });
};

export const handleBookingRequestCheckout = async ({
  accessToken,
  SSOid,
  listing,
  booking,
  formValues,
  paymentMethodId,
  quote,
  initialBookingId
}) => {
  // Steps:
  // 1. Create setup intent
  // 2. Post booking
  // 3. Confirm intent

  const intentPayload = createIntentRequestPayload({
    listing,
    booking,
    formValues,
    paymentMethodId,
    quote
  });

  // If there is no payment method or amount to pay is 0 then there is no need to create intent
  const setupIntentResponse = paymentMethodId && intentPayload.amount !== 0
    ? await createSetupIntent(accessToken, SSOid, intentPayload)
    : null;
  const paymentIntentId = setupIntentResponse?.id;
  // BE might have stored this method before so we need to use it instead
  const newPaymentMethodId = setupIntentResponse?.payment_method || paymentMethodId;

  const requestPayload = createBookingRequestPayload({
    booking,
    paymentMethodId: newPaymentMethodId,
    paymentIntentId,
    formValues
  });
  const bookingResponse = await postBookingRequest(accessToken, requestPayload, initialBookingId);

  try {
    if (paymentIntentId) {
      // Wrapped in its own try catch so we don't stop checkout process if it fails (NO_AUTH)
      await confirmPaymentIntent(SSOid, paymentIntentId, accessToken, {
        ...intentPayload,
        // Payload is the same as for setup intent but amount should be taken from contracts here
        amount: getPaymentAmount(bookingResponse, formValues?.payment?.selectedPaymentOption)
      });
    }
  } catch (e) {
    // Error handled inside confirmPaymentIntent method
  }

  return bookingResponse;
};
